import React, { createContext } from 'react';
import { action, observable, runInAction, makeObservable } from 'mobx';
import type { NavigateFunction } from 'react-router-dom';
import { CHECK_IN_ALERT_CONFIG } from '../CheckInUtils';
import * as CheckInUtils from '../CheckInUtils';
import { store } from 'Models/Store';
import { CheckInLocationState } from 'Models/_HumanWritten/LocationState';
import { getBooking, getCargoTypesForCheckIn, updateCheckedInStatus } from 'Services/Api/_HumanWritten/CheckInService';
import type { AnytimeRedemptionPreview } from 'Services/Api/_HumanWritten/AnytimeTicketService';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { BookingFormState, type IBookingFormState } from './BookingFormState';
import { FerryTripDto } from '../CheckInEntities/FerryTripDto';
import { CheckInBookingOverviewDto } from '../CheckInEntities/CheckInBookingOverviewDto';
import { type BookingFormModeMutation } from '../BookingForm/BookingFormEdit/BookingFormEdit';
import { transformBookingToBookingFormState } from '../BookingForm/BookingFormUtils';
import FerryCheckInSocket from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/context/FerryCheckInSocket';
import {
	AdditionalBookingSectionEntity,
	FerryTripEntity,
	TowOnTypeEntity,
	AdditionalBookingOptionEntity,
	CheckInStatusEntity,
	CargoCharacteristicEntity,
} from 'Models/Entities';
import { getAdditionalBookingSections, getTowOnTypesOptimized } from 'Util/_HumanWritten/OptimizedQueriesUtils';
import CheckInCache from './CheckInCache';
import alertToast from 'Util/ToastifyUtils';
import { sortByFullName } from '../Utils/SorterHelpers/CheckInSorters';
import { bookingType, bookingTypeOptions } from 'Models/Enums';
import { CheckInScanSource, PlannedSeatDto } from 'Models/Dtos/BookingDtos';
import { getMessageByKey } from 'Stores/LanguageStore';
import Icon from '../../../Icon/Icon';
import { FreightIcon } from '../../../Icon/IconHelpers';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { useStatusFilterSettings } from '../CheckInSortAndFilterHelpers/StatusFilter';
import { usePassengerClassFilter } from '../CheckInSortAndFilterHelpers/PassengerClassFilter';
import { CargoTypeForCheckInDto } from '../Types/CargoTypeForCheckInDto';
import { CapacityTypeForCheckInDto } from '../Types/CapacityTypeForCheckInDto';
import { isCapacityTypeVisibleToStaff } from '../Utils/CargoCharacteristicMatching';
import PassengerIcon from 'Views/Components/_HumanWritten/Icon/PassengerIcon';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { confirmUnpaidCheckIn } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/Modals/ConfirmUnpaidCheckInModal';

export interface checkInBookingOptions {
	refresh?: boolean;
	triggerAlert?: boolean;

	// Skip sending a request to the server to update the booking (e.g. when only the client needs to update the display)
	skipUpdateServer?: boolean;
	confirmAndCheckIn?: boolean;

	// Skip the unpaid-booking gate (payment/confirm prompt). Set by callers that have already resolved
	// the unpaid decision up front (e.g. the trip-move flow), so the downstream check-in doesn't
	// prompt a second time. Distinct from skipUpdateServer, which updateCheckInStatusToConsecutiveStatus
	// recomputes from confirmAndCheckIn.
	skipUnpaidGate?: boolean;

	// How a scanner found the booking, recorded on the status history. Absent for a tap.
	source?: CheckInScanSource;
}

/**
 * A request to settle an unpaid booking's payment before it can be checked in. Set by the store
 * when blockCheckInUntilPaid is on; a host inside the check-in page (CheckInView) renders the
 * payment modal and resolves it. Lives in the page tree (not the global modal) because the payment
 * form needs the Router/providers.
 */
export interface PendingPaymentRequest {
	bookingId: string;
	ownerUserId: string;
	unpaidValue: number;
	resolve: (paid: boolean) => void;
}

/**
 * Seats a confirmation screen offered one booking, waiting to be sent with its check-in.
 *
 * <p>The confirmation is shown before the check-in runs, so the seats on it are a plan rather than a
 * fact. Carrying the plan through to the request is what makes the two agree: the deckhand reads a
 * seat out, and that is the seat written, not one the server picked a second time.</p>
 */
export interface PendingSeatPlan {
	bookingId: string;
	seats: PlannedSeatDto[];
}

/** Outcome of the full-page check-in confirmation view. */
export type CheckInConfirmResult = 'checkin' | 'edit' | 'cancel';

export interface PendingConfirmRequest {
	booking: CheckInBookingOverviewDto;
	/** Primary action label, e.g. the booking type's actionString. */
	actionLabel: string;
	/**
	 * True when this confirmation is checking somebody IN, rather than backing a check-in out.
	 *
	 * <p>The same screen asks both questions. Only the forward one previews the seats the booking is
	 * about to be given — offering seats to a booking being taken back off the vessel would be
	 * nonsense.</p>
	 */
	checkingIn: boolean;
	resolve: (result: CheckInConfirmResult) => void;
}

export interface PendingAnytimeConfirmRequest {
	preview: AnytimeRedemptionPreview;
	/** Primary action label, e.g. "Redeem". */
	actionLabel: string;
	title: string;
	/** Resolves true to redeem, false to cancel (anytime tickets have no edit). */
	resolve: (confirmed: boolean) => void;
}

export interface ICheckInStore {
	ferryTripId: string;
	ferryTrip: FerryTripDto;
	/**
	 * The trip id the check-in-time buffer warning has already been evaluated for this visit.
	 * Lets the summary page warn once per fresh entry rather than on every summary/list toggle.
	 * Cleared on {@link dispose} (i.e. when leaving check-in).
	 */
	bufferWarningEvaluatedTripId?: string;
	markCheckInBufferWarningEvaluated: (tripId: string) => void;
	bookings: CheckInBookingOverviewDto[];
	formState: IBookingFormState;
	showFilters: boolean;
	/** Pending unpaid-booking payment request awaiting the in-page payment modal (see above). */
	pendingPaymentRequest: PendingPaymentRequest | null;
	requestUnpaidPayment: (args: { bookingId: string; ownerUserId: string; unpaidValue: number }) => Promise<boolean>;
	resolvePendingPayment: (paid: boolean) => void;
	/** Pending booking awaiting the full-page check-in confirmation view (see CheckInConfirmModalHost). */
	pendingConfirmRequest: PendingConfirmRequest | null;
	requestCheckInConfirmation: (args: {
		booking: CheckInBookingOverviewDto;
		actionLabel: string;
		checkingIn: boolean;
	}) => Promise<CheckInConfirmResult>;
	resolveCheckInConfirmation: (result: CheckInConfirmResult) => void;
	/**
	 * The seats the check-in confirmation has just offered a booking, kept between the screen showing
	 * them and the check-in that writes them.
	 *
	 * <p>Keyed by booking so a plan cannot leak onto the next one the deckhand opens. Null whenever
	 * nothing has been offered — which is every path that does not show the confirmation.</p>
	 */
	pendingSeatPlan: PendingSeatPlan | null;
	setPendingSeatPlan: (plan: PendingSeatPlan | null) => void;
	/** Pending anytime-ticket redemption awaiting the full-page view (see AnytimeRedeemConfirmModalHost). */
	pendingAnytimeConfirmRequest: PendingAnytimeConfirmRequest | null;
	requestAnytimeConfirmation: (args: {
		preview: AnytimeRedemptionPreview;
		actionLabel: string;
		title: string;
	}) => Promise<boolean>;
	resolveAnytimeConfirmation: (confirmed: boolean) => void;
	/**
	 * Gate an unpaid booking before a forward check-in action (status change or trip move).
	 * Returns 'proceed' to continue or 'abort' to stop. blockCheckInUntilPaid ON → payment is
	 * required first; OFF → offer take-payment / proceed-anyway / cancel.
	 */
	resolveUnpaidBeforeCheckIn: (args: {
		bookingId: string;
		ownerUserId: string;
		unpaidValue: number;
		onPaid?: () => void;
	}) => Promise<'proceed' | 'abort'>;
	/**
	 * True if the loaded ferry trip has not departed based on the trip's departure time OR the staff member has
	 * confirmed that it is ok to board a departed trip. When false, a warning popup will appear when
	 * 'Scan QR code' button is clicked.
	 */
	skipFerryHasDepartedModal: boolean;
	usingAddFlow: boolean;
	/**
	 * True if there are unsaved changes in the booking form edit.
	 */
	hasUnsavedBookingChanges: boolean;
	setHasUnsavedBookingChanges: (value: boolean) => void;

	/**
	 * Load the ferry trip and booking entities. This is usually only triggered once during start of CheckInPage.
	 * @param id The ferry trip id.
	 */
	loadFerryTrip: (id: string) => Promise<void>;
	setBookings: (x: CheckInBookingOverviewDto[]) => void;
	removeBooking: (id: string) => void;
	/**
	 * Update `skipFerryHasDepartedModal` to true so that the popup doesn't appear again.
	 */
	confirmCheckInForDepartedTrip: () => void;
	/**
	 * Process the form data to...
	 * Then redirect user to review page.
	 * @param mode Process the form as Add or Edit booking
	 * @param applyToReturn Only applicable for Edit booking where return trip is to be updated.
	 */
	reviewBooking: (
		selectedStopId: string,
		mode: BookingFormModeMutation,
		formState: IBookingFormState,
		hasVehicle: boolean,
		hasTowOn: boolean,
		hasAddOns: boolean,
		navigate: NavigateFunction,
		applyToReturn?: boolean,
	) => void;

	updateCheckInStatusToManualStatus: (
		id: string,
		statusId: string | null,
		stopId: string,
		options?: checkInBookingOptions,
	) => Promise<void>;
	updateCheckInStatusToConsecutiveStatus: (
		id: string,
		next: boolean,
		stopId?: string,
		options?: checkInBookingOptions,
	) => Promise<void>;

	checkInStatuses: { [key: string]: CheckInStatusEntity[] };

	/**
	 * If no booking is provided, a blank booking will be used with ferry trip pre-filled.
	 * Otherwise, the given booking will be used to construct the form state.
	 * Unless, the email is same as booking, then the existing form state is preserved.
	 * @param booking The booking to be transformed into the formState.
	 */
	setFormState: (booking?: CheckInBookingOverviewDto) => void;
	setShowFilters: (showFilters: boolean) => void;
	/**
	 * Access to all vehicle types is frequently required. We will cache after the first request.
	 */
	getAllVehicleTypes: () => Promise<CargoTypeForCheckInDto[]>;
	/**
	 * Maps the CargoType by id.
	 */
	getVehicleTypeMap: () => Map<string, CargoTypeForCheckInDto>;
	/**
	 * Access to all tow on types is frequently required. We will cache after the first request.
	 */
	getAllTowOnTypes: () => Promise<TowOnTypeEntity[]>;
	/**
	 * Access to all additional booking sections and options is frequently required.
	 * We will cache after the first request.
	 */
	getAllAdditionalBookingSections: () => Promise<AdditionalBookingSectionEntity[]>;
	allAdditionalBookingOptions?: AdditionalBookingOptionEntity[];

	capacityTypes: CapacityTypeForCheckInDto[];

	/**
	 * Filter capacity types to include only those with vehicle-related capacity
	 * and their corresponding vehicle-related capacity restrictions.
	 */
	getVehicleCapacityTypes: () => CapacityTypeForCheckInDto[];

	cargoCharacteristics: CargoCharacteristicEntity[];

	getCheckInConnection: () => FerryCheckInSocket;

	/**
	 * Calls `listener` whenever another device changes something on this trip's check-in. Returns the
	 * unsubscribe. The check-in History screen uses it to say "N new" without refetching under the reader.
	 */
	onCheckInActivity: (listener: () => void) => () => void;
	notifyCheckInActivity: () => void;

	getCheckInCache: () => CheckInCache;
	isOffline: () => boolean;

	dispose: () => void;

	getCheckInStatusesByBookingType: (type: bookingType, defaultStatusOnly?: boolean) => CheckInStatusEntity[];
	getCheckInStatusActionsAndCountByBookingType: (
		type: bookingType,
		filteredBookings: CheckInBookingOverviewDto[],
	) => {
		checkInStatus: CheckInStatusEntity;
		count: number;
	}[];
	getCheckInStatusesAndCountByBookingType: (
		type: bookingType,
		filteredBookings: CheckInBookingOverviewDto[],
	) => {
		checkInStatus: CheckInStatusEntity;
		count: number;
	}[];
	getCheckInStatusesAndCountForEachBookingType: (filteredBookings: CheckInBookingOverviewDto[]) => {
		bookingType: bookingType;
		icon: JSX.Element;
		checkInStatuses: { checkInStatus: CheckInStatusEntity; count: number }[];
	}[];

	// getFirstCheckInStatusId: (type: bookingType) => string;
	// getStatusToShowForActionByBookingType: (type: bookingType, id: string, filteredBookings: CheckInBookingOverviewDto[]) => string[];

	getLastCheckInStatusId: (type: bookingType) => string;

	getCheckInStatusById: (typeOfBooking: bookingType, id?: string) => CheckInStatusEntity | undefined | null;
	getCheckInStatusLabel: (typeOfBooking: bookingType, id?: string) => string;

	getNextCheckInStatus: (type: bookingType, currentStatusId?: string) => CheckInStatusEntity | undefined | null;
	getNextCheckInStatusLabel: (type: bookingType, currentStatusId?: string) => string;

	getPreviousCheckInStatuses: (type: bookingType, currentStatusId?: string) => CheckInStatusEntity[] | undefined;
	getPreviousCheckInStatusLabel: (type: bookingType, currentStatusId?: string) => string;
}

export class CheckInStore implements ICheckInStore {
	vehicleTypeMap: Map<string, CargoTypeForCheckInDto> = new Map();
	allVehicleTypes?: CargoTypeForCheckInDto[];
	allTowOnTypes?: TowOnTypeEntity[];
	allAdditionalBookingSections?: AdditionalBookingSectionEntity[];
	capacityTypes: CapacityTypeForCheckInDto[];
	cargoCharacteristics: CargoCharacteristicEntity[];

	public allAdditionalBookingOptions?: AdditionalBookingOptionEntity[];

	ferryTripId: string;

	ferryTrip: FerryTripDto;

	bufferWarningEvaluatedTripId?: string;

	checkInStatuses: { [key: string]: CheckInStatusEntity[] };

	bookings: CheckInBookingOverviewDto[];

	formState: IBookingFormState;

	skipFerryHasDepartedModal: boolean;

	usingAddFlow: boolean;

	hasUnsavedBookingChanges: boolean = false;

	showFilters: boolean = false;

	pendingPaymentRequest: PendingPaymentRequest | null = null;

	pendingConfirmRequest: PendingConfirmRequest | null = null;

	pendingSeatPlan: PendingSeatPlan | null = null;

	setPendingSeatPlan(plan: PendingSeatPlan | null) {
		this.pendingSeatPlan = plan;
	}

	pendingAnytimeConfirmRequest: PendingAnytimeConfirmRequest | null = null;

	private checkInSocket: FerryCheckInSocket | undefined;
	private checkInCache: CheckInCache = CheckInCache.getInstance();

	// On the store rather than the socket: the socket is replaced whenever the trip reloads, and a listener
	// registered on the old one would go quiet.
	private checkInActivityListeners = new Set<() => void>();

	public onCheckInActivity = (listener: () => void) => {
		this.checkInActivityListeners.add(listener);
		return () => {
			this.checkInActivityListeners.delete(listener);
		};
	};

	public notifyCheckInActivity = () => {
		this.checkInActivityListeners.forEach(listener => listener());
	};

	public getCheckInCache = () => {
		return this.checkInCache;
	};

	public isOffline = (): boolean => {
		return !this.getCheckInCache().isOnline;
	};

	public isFerryTripCached = async (): Promise<boolean> => {
		return this.getCheckInCache().isFerryTripCached(this.ferryTripId);
	};

	constructor() {
		makeObservable(this, {
			allAdditionalBookingOptions: observable,
			ferryTripId: observable,
			ferryTrip: observable,
			bufferWarningEvaluatedTripId: observable,
			markCheckInBufferWarningEvaluated: action,
			checkInStatuses: observable,
			bookings: observable,
			formState: observable,
			skipFerryHasDepartedModal: observable,
			usingAddFlow: observable,
			showFilters: observable,
			hasUnsavedBookingChanges: observable,
			capacityTypes: observable,
			cargoCharacteristics: observable,
			pendingPaymentRequest: observable,
			pendingConfirmRequest: observable,
			pendingSeatPlan: observable,
			setPendingSeatPlan: action,
			pendingAnytimeConfirmRequest: observable,
			requestUnpaidPayment: action,
			resolvePendingPayment: action,
			requestCheckInConfirmation: action,
			resolveCheckInConfirmation: action,
			requestAnytimeConfirmation: action,
			resolveAnytimeConfirmation: action,
			resolveUnpaidBeforeCheckIn: action,
			setShowFilters: action,
			loadFerryTrip: action,
			setBookings: action,
			confirmCheckInForDepartedTrip: action,
			setFormState: action,
			reviewBooking: action,
			updateCheckInStatusToConsecutiveStatus: action,
			updateCheckInStatusToManualStatus: action,
			updateCheckInStatus: action,
			setHasUnsavedBookingChanges: action,
		});
	}

	public getCheckInConnection() {
		return this.checkInSocket!;
	}

	setShowFilters() {
		this.showFilters = !this.showFilters;
	}

	setHasUnsavedBookingChanges(value: boolean) {
		this.hasUnsavedBookingChanges = value;
	}

	/**
	 * Request payment for an unpaid booking before check-in. Sets the pending request that the
	 * in-page payment-modal host (CheckInView) renders, and resolves once the staff member
	 * completes payment (true) or cancels (false).
	 */
	requestUnpaidPayment(args: { bookingId: string; ownerUserId: string; unpaidValue: number }): Promise<boolean> {
		return new Promise<boolean>(resolve => {
			runInAction(() => {
				this.pendingPaymentRequest = { ...args, resolve };
			});
		});
	}

	/** Resolve and clear the pending payment request (idempotent). */
	resolvePendingPayment(paid: boolean) {
		const request = this.pendingPaymentRequest;
		this.pendingPaymentRequest = null;
		request?.resolve(paid);
	}

	/**
	 * Show the full-page check-in confirmation view for a booking and resolve once the staff member
	 * chooses to check in, edit, or cancel. Mirrors requestUnpaidPayment: the in-page host
	 * (CheckInConfirmModalHost) renders the view from this pending request.
	 */
	requestCheckInConfirmation(args: {
		booking: CheckInBookingOverviewDto;
		actionLabel: string;
		checkingIn: boolean;
	}): Promise<CheckInConfirmResult> {
		return new Promise<CheckInConfirmResult>(resolve => {
			runInAction(() => {
				this.pendingConfirmRequest = { ...args, resolve };
			});
		});
	}

	/** Resolve and clear the pending confirmation request (idempotent). */
	resolveCheckInConfirmation(result: CheckInConfirmResult) {
		const request = this.pendingConfirmRequest;
		this.pendingConfirmRequest = null;
		request?.resolve(result);
	}

	/**
	 * Show the full-page anytime-ticket redemption confirmation and resolve once the staff member
	 * chooses to redeem or cancel. Rendered by AnytimeRedeemConfirmModalHost.
	 */
	requestAnytimeConfirmation(args: {
		preview: AnytimeRedemptionPreview;
		actionLabel: string;
		title: string;
	}): Promise<boolean> {
		return new Promise<boolean>(resolve => {
			runInAction(() => {
				this.pendingAnytimeConfirmRequest = { ...args, resolve };
			});
		});
	}

	/** Resolve and clear the pending anytime-redemption request (idempotent). */
	resolveAnytimeConfirmation(confirmed: boolean) {
		const request = this.pendingAnytimeConfirmRequest;
		this.pendingAnytimeConfirmRequest = null;
		request?.resolve(confirmed);
	}

	/**
	 * Single chokepoint for the unpaid-booking gate, shared by the check-in status flow and the
	 * trip-move flow so both behave identically. Returns 'proceed' if the caller may continue or
	 * 'abort' if the staff member cancelled.
	 *   - blockCheckInUntilPaid ON  → payment must be taken first (the in-page payment modal).
	 *   - OFF → offer take-payment now / proceed-anyway / cancel.
	 * On a successful payment runs onPaid so the caller can reflect the settled state locally.
	 */
	async resolveUnpaidBeforeCheckIn(args: {
		bookingId: string;
		ownerUserId: string;
		unpaidValue: number;
		onPaid?: () => void;
	}): Promise<'proceed' | 'abort'> {
		const takePayment = async (): Promise<boolean> => {
			const paid = await this.requestUnpaidPayment({
				bookingId: args.bookingId,
				ownerUserId: args.ownerUserId,
				unpaidValue: args.unpaidValue,
			});
			if (paid) {
				args.onPaid?.();
			}
			return paid;
		};

		// Taking payment needs the server, so it isn't an option while offline. Offering it anyway
		// opened the payment modal and failed with an error.
		const canTakePayment = !this.isOffline();

		if (appConfigStore.blockCheckInUntilPaid) {
			if (!canTakePayment) {
				// Payment is enforced and can't be taken — there is no way to complete this check-in
				// until the connection is back.
				alertToast(
					"This booking is unpaid and payment can't be taken while you're offline. " +
						'Check it in once you are back online.',
					'error',
				);
				return 'abort';
			}

			// Payment is enforced — must settle before continuing.
			return (await takePayment()) ? 'proceed' : 'abort';
		}

		// Not enforced — let staff take payment now (when online), continue anyway, or cancel.
		const choice = await confirmUnpaidCheckIn(canTakePayment);
		if (choice === 'cancel') {
			return 'abort';
		}
		if (choice === 'pay') {
			return (await takePayment()) ? 'proceed' : 'abort';
		}
		return 'proceed';
	}

	async loadFerryTrip(id: string) {
		this.ferryTripId = id;

		const cache = this.getCheckInCache();
		if (!cache.isOnline) {
			await cache.connect();

			// `isOnline` starts from `navigator.onLine`, which can be stuck on false while the network
			// works fine, so confirm with the server before serving cached data. Then only use the
			// cache when this trip is actually in it — otherwise fall through to the server, where a
			// genuine outage surfaces as a normal load error instead of a blank ferry trip.
			const isReachable = await cache.verifyConnectivity();
			if (!isReachable && (await cache.isFerryTripCached(id))) {
				// load from the cache instead of the server
				await cache.loadCachedData(this);

				runInAction(() => {
					this.skipFerryHasDepartedModal = new Date() > this.ferryTrip.departureDateTime;
				});
				return;
			}
		}

		const response = await CheckInUtils.fetchCheckInData(id);
		if (!response) {
			throw new Error(`Could not load check-in data for ferry trip: ${id}`);
		}
		runInAction(() => {
			this.ferryTrip = new FerryTripDto(response.ferryTripDto);
			this.bookings = response.bookingSummaries.filter(x => new CheckInBookingOverviewDto(x));
			this.skipFerryHasDepartedModal = new Date() > this.ferryTrip.departureDateTime;
			this.capacityTypes = response.capacityTypes;
			this.cargoCharacteristics = response.cargoCharacteristics;

			this.checkInStatuses = response.checkInStatuses.reduce(
				(acc: { [key: string]: CheckInStatusEntity[] }, status: CheckInStatusEntity) => {
					const key = status.bookingType;

					if (!acc[key]) {
						acc[key] = [];
					}

					acc[key].push(status);
					return acc;
				},
				{},
			);

			// Then sort each group by `order`
			Object.keys(this.checkInStatuses).forEach(key => {
				this.checkInStatuses[key] = this.checkInStatuses[key].slice().sort((a, b) => a.order - b.order);
			});
		});

		const statusFilterState = useStatusFilterSettings.getState();

		// Reset the status filters back to default if the ferry trip has changed
		if (statusFilterState.ferryTripId !== this.ferryTripId) {
			useStatusFilterSettings.setState({
				ferryTripId: this.ferryTripId,
				filterByStatusVehicle: 'booked',
				filterByStatusPassenger: 'booked',
				filterByStatusFreight: 'booked',
			});
		}

		// Fare types belong to one vessel, so a selection carried onto another sailing would match
		// nothing and empty the list. Dropped whenever the trip changes.
		if (usePassengerClassFilter.getState().ferryTripId !== this.ferryTripId) {
			usePassengerClassFilter.getState().resetForTrip(this.ferryTripId);
		}

		// Pre-emptively load cargo types
		try {
			const vehicleTypes = await getCargoTypesForCheckIn();
			this.vehicleTypeMap = new Map(vehicleTypes.map(x => [x.id, x]));
		} catch (e) {
			console.error('Something went wrong while fetching cargo types');
			this.vehicleTypeMap = new Map();
		}

		this.createCheckInSocket();

		// If this ferry trip is cached and there are no offline changes,
		// we can clear the cache and re-cache the updated data.
		//
		// Refreshing the cache is a background side effect: the trip is already loaded and on screen
		// by this point, so an IndexedDB failure here must not reject the load. It used to — which
		// surfaced as "Could not find ferry trip" and a redirect to /404 with nothing in the console
		// or network tab, on a page whose data had loaded perfectly. It then "fixed itself" on the
		// next attempt only because the clear had already run, so the failing branch was skipped.
		try {
			const isCached = await this.isFerryTripCached();
			if (isCached) {
				await cache.clearCachedData(); // Assuming this does not clear booking changes
				await cache.cacheData(this);
			}
		} catch (e) {
			console.error('Failed to refresh the offline check-in cache', e);
		}
	}

	dispose() {
		this.getCheckInConnection()?.closeConnection();
		this.checkInSocket = undefined;
		// A fresh entry into check-in should re-evaluate the check-in-time buffer warning.
		this.bufferWarningEvaluatedTripId = undefined;
	}

	/** Records that the buffer warning has been evaluated for this trip this visit (see the field). */
	markCheckInBufferWarningEvaluated(tripId: string) {
		this.bufferWarningEvaluatedTripId = tripId;
	}

	setBookings(x: CheckInBookingOverviewDto[]) {
		this.bookings = x;
	}

	confirmCheckInForDepartedTrip() {
		this.skipFerryHasDepartedModal = false;
	}

	setFormState(booking?: CheckInBookingOverviewDto, forceReset: boolean = false) {
		if (booking) {
			if (this.usingAddFlow) {
				return;
			}
			if (
				!forceReset &&
				booking.id &&
				this.formState &&
				this.formState.bookingToEdit &&
				this.formState.bookingToEdit === booking.id
			) {
				// While switching between edit and review pages, we don't want to reset the formState
				return;
			}
			this.formState = new BookingFormState(transformBookingToBookingFormState(booking));
			this.formState.bookingBeingEdited = booking;
			return;
		}
		const blankBooking = CheckInBookingOverviewDto.createBlank();
		blankBooking.bookedSummary!.ferryTrip = new FerryTripEntity(this.ferryTrip);
		this.formState = new BookingFormState(transformBookingToBookingFormState(blankBooking));
	}

	async reviewBooking(
		selectedStopId: string,
		mode: BookingFormModeMutation,
		state: IBookingFormState,
		hasVehicle: boolean,
		hasTowOn: boolean,
		hasAddOns: boolean,
		navigate: NavigateFunction,
		applyToReturn = false,
	) {
		this.formState = state;
		this.formState.forEftpos = true;
		//
		// Remove all vehicle details
		// Otherwise assign correct vehicle details
		//
		if (this.formState.type === 'passenger') {
			// Assigned field by field (rather than looping over a list of names) so the compiler
			// checks them: the list previously used 'lengthTypeId' / 'weightTypeId', which are not
			// fields on the form state, so the vehicle length and weight bands were never cleared.
			runInAction(() => {
				this.formState.driverFirstName = undefined;
				this.formState.driverLastName = undefined;
				this.formState.vehicleLengthId = undefined;
				this.formState.vehicleWeightId = undefined;
				this.formState.cargoMake = undefined;
				this.formState.cargoModel = undefined;
				this.formState.cargoTypeId = undefined;
				this.formState.cargoIdentification = undefined;
				this.formState.hiredVehicle = undefined;

				if (appConfigStore.driverPhoneEnabled) {
					this.formState.driverPhone = undefined;
				}
			});
		} else if (this.formState.type === 'vehicle') {
			//
			// 2 things
			//
			//     cargoTypeId must match make and model
			//     hiredVehicle must be assigned a value
			//
			const cargoTypes = await this.getAllVehicleTypes();
			const cargoType = cargoTypes.find(
				x => x.cargoMake === this.formState.cargoMake && x.cargoModel === this.formState.cargoModel,
			);
			runInAction(() => {
				if (cargoType) {
					this.formState.cargoTypeId = cargoType.id;
				}
				this.formState.hiredVehicle = false;
			});
		}

		// Remove all tow on details
		if (this.formState.type !== 'vehicle' || !hasVehicle || !hasTowOn) {
			runInAction(() => {
				this.formState.trailerLengthId = undefined;
				this.formState.trailerTypeId = undefined;
			});
		}

		// Remove all add ons
		if (!hasAddOns) {
			runInAction(() => {
				this.formState.departingTripOptions = [];
			});
		}

		if (!applyToReturn) {
			runInAction(() => {
				this.formState.returningTicketId = undefined;
			});
		}

		const locationState: CheckInLocationState = { mode, applyToReturn: String(applyToReturn) };
		navigate(`/check-in/${this.ferryTripId}/review?stopId=${selectedStopId}`, { state: locationState });
	}

	async updateCheckInStatusToConsecutiveStatus(
		id: string,
		next: boolean,
		stopId?: string,
		options: checkInBookingOptions = {},
	) {
		const { refresh = false, triggerAlert = true, skipUpdateServer = false, confirmAndCheckIn = true } = options;

		let booking: CheckInBookingOverviewDto;
		let added = false;
		const bookingIndex = this.bookings.findIndex(x => x.id === id);
		const isOffline = this.isOffline();
		const isFerryTripCached = await this.isFerryTripCached();

		if (isOffline && !isFerryTripCached) {
			alertToast('Unable to check in booking while offline. Please save data for offline use.', 'error');
			return;
		}

		if (bookingIndex === -1) {
			//
			// Fetch booking from server and add to list
			//
			// Not in the list, so there is no NEW badge to carry across — ask the server for it.
			const newBooking = await getBooking(id, true);
			added = true;
			booking = new CheckInBookingOverviewDto(newBooking.data);
		} else if (refresh && !isOffline) {
			//
			// Refetch same booking for latest data
			//
			const sameBooking = await getBooking(id);
			const newBookingData = new CheckInBookingOverviewDto(sameBooking.data);

			/*
				get-booking-by-id does not answer "first time on this route".

				Working that out for a single booking costs a query over the whole of that user's
				booking history, and this read sits on the critical path twice — opening the sidebar,
				and again the moment a check-in completes. The check-in list already computed it for
				every booking on the trip in one projected query, so the value we hold is both correct
				and free. Carrying it across stops a refetch blanking the NEW badge on the row and in
				the sidebar.
			*/
			newBookingData.firstTimeOnRoute = this.bookings[bookingIndex].firstTimeOnRoute;

			runInAction(() => {
				this.bookings[bookingIndex] = newBookingData;
			});

			if (isFerryTripCached) {
				await this.getCheckInCache().updateBooking(newBookingData);
			}

			booking = this.bookings[bookingIndex];
		} else {
			//
			// Use existing booking
			//
			booking = this.bookings[bookingIndex];
		}

		const lastCheckInStatusId = this.getLastCheckInStatusId(booking.getBookingType());

		const alreadyUsingNextStatus = next && lastCheckInStatusId === booking.checkInStatusId;

		const checkInStatusId = next
			? this.getNextCheckInStatus(booking.getBookingType(), booking.checkInStatusId)?.id
			: this.getPreviousCheckInStatuses(booking.getBookingType(), booking.checkInStatusId)?.filter(
					x => x.default,
				)?.[0]?.id;

		await this.updateCheckInStatus(
			booking,
			(alreadyUsingNextStatus ? booking.checkInStatusId : checkInStatusId) ?? null,
			added,
			isFerryTripCached,
			isOffline,
			{
				...options,
				skipUpdateServer: !confirmAndCheckIn,
				triggerAlert: confirmAndCheckIn,
				confirmAndCheckIn: confirmAndCheckIn && !alreadyUsingNextStatus, // Only update status if confirming AND not already at target
			},
			stopId,
		);
		if (!confirmAndCheckIn) {
			alertToast(
				// Uses driver name, otherwise user name.
				booking.getFullName(),
				'info',
				'Booking updated',
				CHECK_IN_ALERT_CONFIG,
			);
		}
	}

	async updateCheckInStatusToManualStatus(
		id: string,
		statusId: string | null,
		stopId?: string,
		options: checkInBookingOptions = {},
	) {
		const { refresh = false, triggerAlert = true, skipUpdateServer = false, confirmAndCheckIn = true } = options;

		let booking: CheckInBookingOverviewDto;
		let added = false;
		const bookingIndex = this.bookings.findIndex(x => x.id === id);
		const isOffline = this.isOffline();
		const isFerryTripCached = await this.isFerryTripCached();

		if (isOffline && !isFerryTripCached) {
			alertToast('Unable to check in booking while offline. Please save data for offline use.', 'error');
			return;
		}

		if (bookingIndex === -1) {
			//
			// Fetch booking from server and add to list
			//
			// Not in the list, so there is no NEW badge to carry across — ask the server for it.
			const newBooking = await getBooking(id, true);
			added = true;
			booking = new CheckInBookingOverviewDto(newBooking.data);
		} else if (refresh && !isOffline) {
			//
			// Refetch same booking for latest data
			//
			const sameBooking = await getBooking(id);
			const newBookingData = new CheckInBookingOverviewDto(sameBooking.data);

			// BookingAdded / BookingUpdated mean "re-read this booking" but carry nothing about its
			// check-in status (confirmAndCheckIn === false — see onBookingAdded/onBookingUpdated).
			// The refetch races the check-in: a terminal payment now drives completion on the poll
			// (see #1458), so it broadcasts BookingUpdated/BookingAdded *before* the frontend-driven
			// check-in has persisted. This read then comes back not-checked-in and — since it replaces
			// the whole row — overwrites the check-in that was just applied (optimistically on the
			// acting device, or via UpdateBookingStatus on others), leaving the row stuck at Booked
			// until a manual refresh. Preserve the check-in status we already hold; only
			// UpdateBookingStatus or a user action is allowed to change it.
			if (!confirmAndCheckIn) {
				const existing = this.bookings[bookingIndex];
				newBookingData.checkedIn = existing.checkedIn;
				newBookingData.checkInStatusId = existing.checkInStatusId;
			}

			/*
				get-booking-by-id does not answer "first time on this route".

				Working that out for a single booking costs a query over the whole of that user's
				booking history, and this read sits on the critical path twice — opening the sidebar,
				and again the moment a check-in completes. The check-in list already computed it for
				every booking on the trip in one projected query, so the value we hold is both correct
				and free. Carrying it across stops a refetch blanking the NEW badge on the row and in
				the sidebar.
			*/
			newBookingData.firstTimeOnRoute = this.bookings[bookingIndex].firstTimeOnRoute;

			runInAction(() => {
				this.bookings[bookingIndex] = newBookingData;
			});

			if (isFerryTripCached) {
				await this.getCheckInCache().updateBooking(newBookingData);
			}

			booking = this.bookings[bookingIndex];
		} else {
			//
			// Use existing booking
			//
			booking = this.bookings[bookingIndex];
		}

		await this.updateCheckInStatus(booking, statusId, added, isFerryTripCached, isOffline, options, stopId);
	}

	async updateCheckInStatus(
		booking: CheckInBookingOverviewDto,
		statusId: string | null,
		added: boolean,
		isFerryTripCached: boolean,
		isOffline: boolean,
		options: checkInBookingOptions = {},
		stopId?: string,
	) {
		const {
			refresh = false,
			triggerAlert = true,
			skipUpdateServer = false,
			confirmAndCheckIn = true,
			skipUnpaidGate = false,
		} = options;

		const currentStatusOrder =
			this.getCheckInStatusById(booking.getBookingType(), booking.checkInStatusId)?.order ?? 0;
		const newStatusOrder = isNullOrUndefined(statusId)
			? 0
			: (this.getCheckInStatusById(booking.getBookingType(), statusId)?.order ?? 0);
		const nextStatus = newStatusOrder > currentStatusOrder;

		// Checking in an unpaid booking. Only intercept forward progress (checking in) —
		// un-checking/reverting is always allowed. Two scenarios, depending on the site setting:
		//   1. blockCheckInUntilPaid OFF — let staff check in, but confirm they meant to (unpaid).
		//   2. blockCheckInUntilPaid ON  — take payment first, then confirm the check-in.
		// Cancelling at any prompt aborts the check-in.
		// Only prompt for a user-initiated check-in. Socket echoes (the server broadcasting this very
		// change back) and offline cache replays come through with skipUpdateServer — they must not
		// re-trigger the payment/confirm prompts, otherwise the modal re-opens after "Check in anyway"
		// (which leaves the booking unpaid) when the echo arrives.
		const isCheckingIn = isNotNullOrUndefined(statusId) && statusId !== 'booked';
		if (isCheckingIn && !skipUpdateServer && !skipUnpaidGate && booking.transactionStatus === 'UNPAID') {
			const unpaidValue =
				(booking.bookedSummary?.totalCost ?? 0) - (booking.bookedSummary?.paidBookingPrice ?? 0);

			// Shared unpaid gate (take payment / confirm / cancel) — see resolveUnpaidBeforeCheckIn.
			const result = await this.resolveUnpaidBeforeCheckIn({
				bookingId: booking.id,
				ownerUserId: booking.user?.id ?? '',
				unpaidValue,
				// Reflect the settled state locally so the list/UI updates immediately.
				onPaid: () => {
					booking.transactionStatus = 'PAID';
				},
			});
			if (result === 'abort') {
				return;
			}

			// When payment is enforced we just took it above; confirm the staff member still wants
			// to check this booking in now (the move flow has its own up-front confirmation instead).
			if (appConfigStore.blockCheckInUntilPaid) {
				const confirmed = await newConfirmModal(
					'Confirm check-in',
					'Payment received. Check this booking in now?',
					{ confirmText: 'Check in', cancelText: 'Cancel', resolveOnCancel: true },
				).catch(() => false);
				if (!confirmed) {
					return;
				}
			}
		}

		if (isOffline && !isFerryTripCached) {
			alertToast('Unable to check in booking while offline. Please save data for offline use.', 'error');
			return;
		}

		// Update checkedIn status if required
		const checkInStatus =
			isNullOrUndefined(statusId) || statusId === 'booked'
				? null
				: this.getCheckInStatusById(booking.getBookingType(), statusId);

		// Kept so the optimistic change below can be undone if the server rejects it — otherwise a
		// failed write leaves the row showing a check-in that never happened, and staff only find out
		// on the next refresh (or never, if they don't catch the error toast).
		const previousCheckedIn = booking.checkedIn;
		const previousCheckInStatusId = booking.checkInStatusId;

		if (confirmAndCheckIn) {
			booking.checkedIn = checkInStatus?.includedInCapacity ?? false;
			booking.checkInStatusId = statusId === 'booked' || isNullOrUndefined(statusId) ? undefined : statusId;
		}

		if (isOffline && isFerryTripCached) {
			await this.getCheckInCache().setBookingCheckInStatus(
				booking.id,
				this.ferryTripId,
				statusId === 'booked' || isNullOrUndefined(statusId) ? null : statusId,
				stopId ?? null,
				store.userId,
			);
		}

		// Re-render list
		const applyBookingToList = () => {
			const existingIndex = this.bookings.findIndex(b => b.id === booking.id);

			if (existingIndex >= 0) {
				// Replace existing booking
				const updatedBookings = [...this.bookings];
				updatedBookings[existingIndex] = booking;
				this.setBookings(updatedBookings.sort(sortByFullName()));
			} else {
				// Add new booking
				this.setBookings([...this.bookings, booking].sort(sortByFullName()));
			}
		};

		applyBookingToList();

		// No stop guard here. StopId is nullable on the server (CheckInDto.StopId is a Guid?), and some
		// entry points legitimately have no stop context — the QR-code redirect passes none at all. This
		// used to skip the request entirely in that case, so the row flipped to checked-in, the success
		// sound played, and nothing was ever written.
		if (!skipUpdateServer && !isOffline) {
			// The seats the confirmation just offered THIS booking. Sent so the check-in writes the
			// seats the deckhand read out; the server re-checks each one and replaces any that has
			// since gone. Consumed either way, so a stale plan cannot reach the next check-in.
			const seatPlan = this.pendingSeatPlan?.bookingId === booking.id ? this.pendingSeatPlan.seats : undefined;
			this.setPendingSeatPlan(null);

			try {
				await updateCheckedInStatus({
					bookingId: booking.id,
					// Empty string would fail to bind to a Guid? server-side; send a real null.
					stopId: stopId || null,
					ferryTripId: this.ferryTripId,
					routeId: this.ferryTrip.routeId,
					bookingType: booking.getBookingType(),
					statusId: checkInStatus?.id ?? null,
					plannedSeats: seatPlan,
					source: options.source,
				});
			} catch (e) {
				// Put the row back the way it was, then let the caller report the failure. Without this
				// the list keeps the optimistic status and the booking looks checked in when it isn't.
				runInAction(() => {
					booking.checkedIn = previousCheckedIn;
					booking.checkInStatusId = previousCheckInStatusId;
				});
				applyBookingToList();
				throw e;
			}

			if (isFerryTripCached) {
				await this.getCheckInCache().updateBooking(booking);
			}

			/*
				Read the booking back for the seats the check-in just handed out.

				`AssignSeatsAtCheckInUseCase` runs inside the check-in and writes a seat for every
				passenger who arrived without one, but `update-check-in-status` answers a bare `true` —
				so the only way to learn WHICH seats is to ask. Nothing here did, and the row went on
				saying "Seat assigned at check-in" while the seating plan went on drawing those seats
				free, until somebody reloaded the page.

				One read, on the device that pressed the button, and only where the status actually
				puts the booking aboard: backing a check-in out hands nobody a seat, so there is
				nothing to go and look at. And only where the site sells fare types — seats are given
				by fare type, so elsewhere nobody is seated and the read would only delay the alert.
			*/
			if (checkInStatus?.includedInCapacity && appConfigStore.passengerClassesEnabled) {
				try {
					const latest = await getBooking(booking.id);
					const reseated = new CheckInBookingOverviewDto(latest.data);

					// The status this call just wrote wins over whatever the read came back with. The
					// read races the write on the paths where completion is driven by a poll (see the
					// note in updateCheckInStatusToManualStatus), and the status is the one fact this
					// method already knows for certain.
					reseated.checkedIn = booking.checkedIn;
					reseated.checkInStatusId = booking.checkInStatusId;
					// Not returned by the read either — see the note on the refetch above.
					reseated.firstTimeOnRoute = booking.firstTimeOnRoute;

					runInAction(() => {
						const index = this.bookings.findIndex(x => x.id === booking.id);
						if (index >= 0) {
							this.bookings[index] = reseated;
						}
					});

					if (isFerryTripCached) {
						await this.getCheckInCache().updateBooking(reseated);
					}
				} catch (e) {
					// Nothing to undo. The check-in itself succeeded and the row is already right about
					// that; only the seat labels are missing, which is where this screen was before.
					console.error('Could not re-read the booking for its assigned seats', e);
				}
			}
		}

		if (triggerAlert) {
			CheckInUtils.checkInAlert(
				booking,
				this.getCheckInStatusById(booking.getBookingType(), booking.checkInStatusId)?.label ?? 'Booked',
				nextStatus,
			);
		}
	}

	removeBooking(id: string) {
		this.setBookings(this.bookings.filter(x => x.id !== id));
	}

	getVehicleTypeMap() {
		return this.vehicleTypeMap;
	}

	async getAllVehicleTypes() {
		if (this.allVehicleTypes) {
			return Promise.resolve(this.allVehicleTypes);
		}

		try {
			this.allVehicleTypes = await getCargoTypesForCheckIn();
			this.vehicleTypeMap = new Map(this.allVehicleTypes.map(x => [x.id, x]));
		} catch (e) {
			console.error('Something went wrong while fetching cargo types');
			this.allVehicleTypes = [];
		}

		return this.allVehicleTypes;
	}

	async getAllTowOnTypes() {
		if (this.allTowOnTypes) {
			return Promise.resolve(this.allTowOnTypes);
		}

		const result = await getTowOnTypesOptimized();
		this.allTowOnTypes = result;

		return result;
	}

	async getAllAdditionalBookingSections() {
		if (this.allAdditionalBookingSections) {
			return Promise.resolve(this.allAdditionalBookingSections);
		}

		if (isNullOrUndefined(this.ferryTrip.routeId) || isNullOrUndefined(this.ferryTrip.ferryId)) {
			return [];
		}
		const result = await getAdditionalBookingSections({
			ferryIds: [this.ferryTrip.ferryId],
			routeIds: [this.ferryTrip.routeId],
		});
		this.allAdditionalBookingOptions = result.flatMap(x => x.additionalBookingOptions);
		return result;
	}

	getVehicleCapacityTypes() {
		const result: CapacityTypeForCheckInDto[] = [];
		for (const capacityType of checkInStore.capacityTypes) {
			const vehicleRestrictions = capacityType.capacityRestrictions.filter(
				restriction => !!restriction.getConfigurationJsonAsDto().vehicleConfig,
			);

			// Capacity types whose characteristics are all hidden from staff are left out entirely: the
			// vehicles that matched them fall through to their next matching visible type, or to Cars, so
			// nothing goes missing from the load breakdown — only the label does.
			if (vehicleRestrictions.length > 0 && isCapacityTypeVisibleToStaff(capacityType)) {
				result.push(capacityType);
			}
		}
		// Render in ascending configured order so the vehicle load table shows capacity types in a
		// consistent order and each vehicle/trailer falls under the last (greatest) matching type.
		return result.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}

	private async createCheckInSocket() {
		if (this.checkInSocket) {
			// Keep the existing socket only when it is for this ferry trip AND still alive. Matching
			// on the id alone kept a dead socket forever: once the connection had closed for good this
			// returned early, so even a manual refresh left the page deaf to check-in events.
			if (this.checkInSocket.getFerryTripId() === this.ferryTripId && this.checkInSocket.isLive()) {
				return;
			}

			await this.checkInSocket.closeConnection();
		}
		this.checkInSocket = new FerryCheckInSocket(this);
	}

	public getCheckInStatusesByBookingType(type: bookingType, defaultStatusOnly: boolean = true) {
		const statusList = [];
		const statuses = (this.checkInStatuses[type] ?? []).slice().sort((a, b) => a.order - b.order);

		if (statuses.length > 0) {
			statusList.push(
				new CheckInStatusEntity({
					id: 'booked',
					order: 0,
					label: getMessageByKey('check-in-status__booked-status__label', 'Booked'),
					abbreviation: 'B',
					includedInCapacity: false,
					disembarked: false,
					confirmBooking: false,
					default: true,
					bookingType: type,
					colour: appConfigStore.bookedStatusTagColour,
					actionString: '',
				}),
			);
		}

		for (const status of statuses) {
			statusList.push(status);
		}

		if (defaultStatusOnly) {
			return statusList.filter(x => x.default);
		}
		return statusList;
	}

	public getCheckInStatusActionsAndCountByBookingType(
		type: bookingType,
		filteredBookings: CheckInBookingOverviewDto[],
	): {
		checkInStatus: CheckInStatusEntity;
		count: number;
	}[] {
		const statusList = [];
		const allStatuses = this.getCheckInStatusesByBookingType(type, false);
		const defaultStatusesOnly = allStatuses.filter(x => x.default);
		const lastStatus = defaultStatusesOnly[defaultStatusesOnly.length - 1]?.id;

		for (const status of defaultStatusesOnly) {
			const previousStatuses = allStatuses.filter(x => (x.order ?? 0) === status.order - 1).map(x => x.id);

			statusList.push({
				checkInStatus: status,
				count: filteredBookings.filter(x => previousStatuses.includes(x.checkInStatusId ?? '')).length,
			});
		}

		const completeStatus = [
			{
				checkInStatus: new CheckInStatusEntity({
					id: 'complete',
					order: 0,
					label: 'Completed',
					abbreviation: '✓',
					includedInCapacity: false,
					disembarked: false,
					confirmBooking: true,
					default: true,
					bookingType: type,
					colour: '#81C784',
					actionString: 'Complete',
				}),
				count: this.bookings
					.filter(x => x.getBookingType() === type)
					.filter(x => x.checkInStatusId === lastStatus).length,
			},
		];

		statusList.push(...completeStatus);

		return statusList.filter(x => x.checkInStatus.default || x.count > 0);
	}

	public getCheckInStatusesAndCountByBookingType(
		type: bookingType,
		filteredBookings: CheckInBookingOverviewDto[],
	): {
		checkInStatus: CheckInStatusEntity;
		count: number;
	}[] {
		const statusList = [];
		const statuses = this.getCheckInStatusesByBookingType(type, false);

		for (const status of statuses) {
			statusList.push({
				checkInStatus: status,
				count:
					status.id === 'booked'
						? filteredBookings
								.filter(x => x.getBookingType() === type)
								.filter(x => isNullOrUndefined(x.checkInStatusId)).length
						: filteredBookings
								.filter(x => x.getBookingType() === type)
								.filter(x => x.checkInStatusId === status?.id).length,
			});
		}

		return statusList.filter(x => x.checkInStatus.default || x.count > 0);
	}

	public getCheckInStatusesAndCountForEachBookingType(filteredBookings: CheckInBookingOverviewDto[]) {
		const statusList = [];
		let bookingTypes = Object.keys(bookingTypeOptions) as bookingType[];

		if (!appConfigStore.passengerOnlyBookingEnabled) {
			bookingTypes = bookingTypes.filter(x => x !== 'PASSENGER_ONLY');
		}

		if (!appConfigStore.vehicleEnabled) {
			bookingTypes = bookingTypes.filter(x => x !== 'VEHICLE');
		}

		if (!appConfigStore.freightEnabled) {
			bookingTypes = bookingTypes.filter(x => x !== 'FREIGHT');
		}

		for (const type of bookingTypes) {
			const statuses = this.getCheckInStatusesAndCountByBookingType(type as bookingType, filteredBookings);
			let icon = <></>;
			if (type === 'VEHICLE') {
				icon = <Icon name="directions_car" useMaterialIcons />;
			}

			if (type === 'PASSENGER_ONLY') {
				icon = <PassengerIcon />;
			}

			if (type === 'FREIGHT') {
				icon = <FreightIcon />;
			}

			statusList.push({
				bookingType: type as bookingType,
				checkInStatuses: statuses,
				icon: icon,
			});
		}

		let customOrder = ['PASSENGER_ONLY', 'VEHICLE', 'FREIGHT'];

		if (whiteLabelStore.defaultBookingType === 'vehicle') {
			customOrder = ['VEHICLE', 'PASSENGER_ONLY', 'FREIGHT'];
		}

		if (whiteLabelStore.defaultBookingType === 'freight') {
			customOrder = ['VEHICLE', 'PASSENGER_ONLY', 'FREIGHT'];
		}

		// Sort the statusList based on the custom order
		return statusList.sort((a, b) => {
			const indexA = customOrder.indexOf(a.bookingType);
			const indexB = customOrder.indexOf(b.bookingType);

			// If both booking types are in the custom order array, compare their positions
			return indexA - indexB;
		});
	}

	public getCheckInStatusById(typeOfBooking: bookingType, id?: string) {
		if (!id) {
			return null;
		}

		return this.checkInStatuses[typeOfBooking]?.find(x => x.id === id);
	}

	public getCheckInStatusLabel(typeOfBooking: bookingType, id?: string) {
		const status = this.getCheckInStatusById(typeOfBooking, id);
		return status?.label ?? getMessageByKey('check-in-status__booked-status__label', 'Booked');
	}

	public getNextCheckInStatus(type: bookingType, currentStatusId?: string) {
		const statuses = this.getCheckInStatusesByBookingType(type, false);
		const currentOrder = statuses.find(x => x.id === currentStatusId)?.order ?? 0;

		return statuses.filter(x => x.default).find(x => x.order === currentOrder + 1);
	}

	public getNextCheckInStatusLabel(type: bookingType, currentStatusId?: string) {
		return this.getNextCheckInStatus(type, currentStatusId)?.label ?? 'Complete';
	}

	public getPreviousCheckInStatuses(type: bookingType, currentStatusId?: string) {
		const statuses = this.getCheckInStatusesByBookingType(type, false);
		const currentOrder = statuses.find(x => x.id === currentStatusId)?.order ?? 0;
		return statuses.filter(x => x.order === currentOrder - 1);
	}

	public getPreviousCheckInStatusLabel(type: bookingType, currentStatusId?: string): string {
		return (
			this.getPreviousCheckInStatuses(type, currentStatusId)?.filter(x => x.default)?.[0]?.label ??
			getMessageByKey('check-in-status__booked-status__label', 'Booked')
		);
	}

	public getLastCheckInStatusId(type: bookingType) {
		return this.getCheckInStatusesByBookingType(type).sort((a, b) => b.order - a.order)[0].id;
	}
}

/**
 * This should only be used in the root of the component tree that uses CheckInStoreContext.Provider.
 *
 * Otherwise, use useCheckInStore hook to access check-in related data.
 */
export const checkInStore: ICheckInStore = new CheckInStore();
export const CheckInStoreContext = createContext<ICheckInStore>(checkInStore);
